// Data transformation utilities
export const convertObjectToArray = (obj: any) => {
    return Object.keys(obj).map((key) => ({
        key,
        value: obj[key],
    }));
};

export const convertArrayToObject = (arr: any) => {
    if (!Array.isArray(arr)) {
        return arr || {};
    }
    return arr.reduce(
        (acc: any, item: any) => ({
            ...acc,
            [item.key]: item.value,
        }),
        {}
    );
};

export const convertDataStoresToArray = (obj: any) => {
    return Object.keys(obj).map((key) => {
        if (typeof obj[key] === 'string') {
            return {
                id: key,
                path: obj[key],
            };
        } else if (typeof obj[key] === 'object') {
            const { path, addLastModified, addETag, cacheControl } = obj[key];
            return {
                id: key,
                path,
                addLastModified,
                addETag,
                cacheControl,
            };
        }
        return {
            key,
            value: obj[key],
        };
    });
};

export const convertArrayToDataStores = (arr: any) => {
    if (!Array.isArray(arr)) {
        return arr || {};
    }
    return arr.reduce(
        (acc: any, item: any) => ({
            ...acc,
            [item.id]: {
                path: item.path,
                addLastModified: item.addLastModified,
                addETag: item.addETag,
                cacheControl: item.cacheControl,
            },
        }),
        {}
    );
};

export const processSettingsBeforeDisplay = (settings: any) => {
    if (!settings) return {};

    const processedSettings = { ...settings };
    const newEnvironments: any[] = [];

    processedSettings.environments?.forEach((env: any) => {
        const newEnv = {
            ...env,
            dataSources: convertObjectToArray(env.dataSources || {}),
            virtualDirs: convertObjectToArray(env.virtualDirs || {}),
        };
        newEnvironments.push(newEnv);
    });

    processedSettings.environments = newEnvironments;
    processedSettings.dataStores = convertDataStoresToArray(processedSettings.dataStores || {});

    return processedSettings;
};

export const processSettingsBeforeSave = (settings: any) => {
    if (!settings) return {};

    const processedSettings = { ...settings };
    const newEnvironments: any[] = [];

    processedSettings.environments?.forEach((env: any) => {
        const newEnv = {
            ...env,
            dataSources: convertArrayToObject(env.dataSources || []),
            virtualDirs: convertArrayToObject(env.virtualDirs || []),
        };
        newEnvironments.push(newEnv);
    });

    processedSettings.environments = newEnvironments;
    processedSettings.dataStores = convertArrayToDataStores(processedSettings.dataStores || []);

    return processedSettings;
};

// Get environment-specific data for CSP sub-tabs
export const getEnvironmentSpecificData = (processedSettings: any, envId: string) => {
    if (!processedSettings?.environments) return {};

    const specificEnv = processedSettings.environments.find((env: any) => env.id === envId);
    if (!specificEnv) return {};

    return {
        environments: [specificEnv],
    };
};

// Utility function to deep compare objects and get only changed properties
export const getChangedProperties = (original: any, current: any): any => {
    const changes: any = {};

    if (!original) return current;
    if (!current) return {};

    for (const key in current) {
        if (Object.prototype.hasOwnProperty.call(current, key)) {
            if (Array.isArray(current[key]) && Array.isArray(original[key])) {
                if (JSON.stringify(current[key]) !== JSON.stringify(original[key])) {
                    changes[key] = current[key];
                }
            } else if (Array.isArray(current[key]) && !Array.isArray(original[key])) {
                changes[key] = current[key];
            } else if (
                typeof current[key] === 'object' &&
                current[key] !== null &&
                typeof original[key] === 'object' &&
                original[key] !== null
            ) {
                const nestedChanges = getChangedProperties(original[key], current[key]);
                if (Object.keys(nestedChanges).length > 0) {
                    changes[key] = current[key];
                }
            } else if (
                typeof current[key] === 'object' &&
                current[key] !== null &&
                (typeof original[key] !== 'object' || original[key] === null)
            ) {
                changes[key] = current[key];
            } else if (current[key] !== original[key]) {
                changes[key] = current[key];
            }
        }
    }

    return changes;
};
